import { useRef } from "react";
import { useParams } from "react-router-dom";
import {
  BrandingProvider,
  LeaderboardList,
  LeaderboardRow,
  LeaderboardShell,
  formatLapTime,
  leaderboardCarLabels,
  useOrientation,
} from "@vms/leaderboard-ui";
import type { LeaderboardEntry, RaceStanding } from "@vms/types";
import {
  usePublicEventConfig,
  usePublicEventEntries,
  usePublicEventSectors,
  usePublicLeaderboardLive,
} from "../../api/public-event";

/**
 * Public, key-gated per-event leaderboard — the "anyone, anywhere" view of an
 * event's live standings, reachable at `/l/:eventKey`. Mounted OUTSIDE the
 * cloud-ui shell (no sidebar/topbar, no login) and served from the cloud's
 * hub-master mirror. Mirrors the authed `display/HotlapDisplay` +
 * `display/RaceDisplay`, but:
 *   - resolves the event by its `public_key` instead of the internal id,
 *   - is LIVE over the public WebSocket (the cloud pushes the recomputed board
 *     on change — scalable for many viewers), falling back to polling when the
 *     socket is down,
 *   - is mode-aware in one page (hotlap best-lap vs race standings).
 */
export function PublicEventLeaderboard() {
  const { eventKey } = useParams<{ eventKey: string }>();
  const { isLandscape } = useOrientation();
  const prevEntriesRef = useRef<LeaderboardEntry[]>([]);

  const { data: config, notFound } = usePublicEventConfig(eventKey);
  // Live push over the public WS; poll only as a fallback while it's down.
  const { data: live, sectors: liveSectors, connected } = usePublicLeaderboardLive(eventKey);
  const { data: polled } = usePublicEventEntries(eventKey, connected ? 0 : 8000);
  const { data: polledSectors } = usePublicEventSectors(eventKey, connected ? 0 : 8000);
  const entriesData = connected && live ? live : polled;
  const sectors = connected && liveSectors ? liveSectors : polledSectors;

  if (notFound) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gray-950 p-8 text-center text-gray-300">
        <div>
          <p className="text-xl font-semibold">Leaderboard unavailable</p>
          <p className="mt-2 text-sm text-gray-500">
            This link is invalid or the event is no longer public.
          </p>
        </div>
      </div>
    );
  }

  const mode = config?.event.mode ?? entriesData?.mode ?? "hotlap";
  const title = config?.event.name ?? "Leaderboard";
  const subtitle = config?.event.trackName ?? config?.event.track ?? undefined;

  return (
    <BrandingProvider branding={config?.branding ?? null}>
      <LeaderboardShell title={title} subtitle={subtitle} mode={mode}>
        {mode === "race"
          ? renderRace(
              (entriesData?.entries as RaceStanding[]) ?? [],
              config?.event.carsAvailable,
            )
          : renderHotlap(
              (entriesData?.entries as LeaderboardEntry[]) ?? [],
              sectors,
              isLandscape,
              prevEntriesRef,
              config?.event.carsAvailable,
            )}
      </LeaderboardShell>
    </BrandingProvider>
  );
}

function renderHotlap(
  leaderboard: LeaderboardEntry[],
  sectors: { sector1Ms: number | null; sector2Ms: number | null; sector3Ms: number | null } | null,
  isLandscape: boolean,
  prevEntriesRef: React.MutableRefObject<LeaderboardEntry[]>,
  availableCars?: string[],
) {
  if (leaderboard.length === 0) {
    return (
      <div className="flex h-full items-center justify-center text-lg text-gray-500">
        No lap times recorded yet.
      </div>
    );
  }

  // Show the car (model name only) only when the event offers 2+ car models.
  const carLabels = leaderboardCarLabels(leaderboard, availableCars);

  // Pulse on lap-time / position improvement since the previous poll.
  const prevMap = new Map(
    prevEntriesRef.current.map((e) => [`${e.driver.id}:${e.car}`, e]),
  );
  const pulseSet = new Set<string>();
  for (const entry of leaderboard) {
    const key = `${entry.driver.id}:${entry.car}`;
    const prev = prevMap.get(key);
    if (prev && (entry.bestLapTimeMs < prev.bestLapTimeMs || entry.position < prev.position)) {
      pulseSet.add(key);
    }
  }
  if (leaderboard.length > 0) prevEntriesRef.current = leaderboard;

  return (
    <LeaderboardList
      totalCount={leaderboard.length}
      renderEntry={(index) => {
        const entry = leaderboard[index];
        const key = `${entry.driver.id}:${entry.car}`;
        return (
          <LeaderboardRow
            position={entry.position}
            driverName={`${entry.driver.firstName} ${entry.driver.lastName}`}
            companyName={entry.driver.companyName || undefined}
            car={carLabels[index]}
            lapTime={formatLapTime(entry.bestLapTimeMs)}
            sectors={{
              s1: entry.bestLapSector1Ms,
              s2: entry.bestLapSector2Ms,
              s3: entry.bestLapSector3Ms,
            }}
            fastestSectors={sectors ?? undefined}
            showSectors={isLandscape}
            pulse={pulseSet.has(key)}
          />
        );
      }}
    />
  );
}

function renderRace(entries: RaceStanding[], availableCars?: string[]) {
  if (entries.length === 0) {
    return (
      <div className="flex h-full items-center justify-center text-lg text-gray-500">
        Race not started.
      </div>
    );
  }
  const carLabels = leaderboardCarLabels(entries, availableCars);
  return (
    <LeaderboardList
      totalCount={entries.length}
      renderEntry={(index) => {
        const entry = entries[index];
        const gapStr =
          entry.gapToLeaderMs == null ? "" : `+${formatLapTime(entry.gapToLeaderMs)}`;
        const extraColumns = [
          { label: "Laps", value: String(entry.lapsCompleted) },
          { label: "Gap", value: gapStr },
          { label: "Last", value: entry.lastLapTimeMs ? formatLapTime(entry.lastLapTimeMs) : "—" },
          { label: "Best", value: entry.bestLapTimeMs ? formatLapTime(entry.bestLapTimeMs) : "—" },
        ];
        return (
          <LeaderboardRow
            position={entry.position}
            driverName={`${entry.driver.firstName} ${entry.driver.lastName}`}
            companyName={entry.driver.companyName || undefined}
            car={carLabels[index]}
            lapTime={formatLapTime(entry.totalTimeMs)}
            showSectors={false}
            extraColumns={extraColumns}
          />
        );
      }}
    />
  );
}
